import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { UseRootStore } from "@/model/RootStore"
import * as apiClient from "@/utils/ApiClient"

type ProfilePhoneInfo = {
	phone?: string | null
	phoneNumber?: string | null
	phoneVerifiedAt?: Date | string | null
	phone_verified_at?: Date | string | null
}

export enum PhoneStep {
	Edit = "edit",
	Verify = "verify",
	Confirm = "confirm",
	Done = "done",
}

const minimumPhoneDigits = 10

const normalizePhoneNumberForComparison = (phoneNumber: string | null | undefined): string =>
	(phoneNumber ?? "").trim().replace(/[\s().-]/g, "")

const formatPhoneNumberForDisplay = (phoneNumber: string | null | undefined): string => {
	const normalized = normalizePhoneNumberForComparison(phoneNumber)
	const northAmericanMatch = normalized.match(/^\+1(\d{3})(\d{3})(\d{4})$/)
	return northAmericanMatch
		? `+1-${northAmericanMatch[1]}-${northAmericanMatch[2]}-${northAmericanMatch[3]}`
		: phoneNumber ?? ""
}

export function usePhoneVerification() {
	const store = UseRootStore()
	const { t, locale } = useI18n()

	const phoneNumberDraft = ref("")
	const phoneVerificationCode = ref("")
	const phoneVerificationRequestedFor = ref<string | null>(null)
	const phoneVerificationMessage = ref<string | null>(null)
	const phoneVerificationError = ref<string | null>(null)
	const isSavingPhoneNumber = ref(false)
	const isStartingPhoneVerification = ref(false)
	const isConfirmingPhoneVerification = ref(false)
	const isEditingPhoneNumber = ref(false)

	const currentUserDetails = computed(() => store.CurrentUserBasicDetails ?? store.CurrentUserDetails)
	const userInfo = computed(() => store.currentUser.getInfo())
	const profilePhoneInfo = computed<ProfilePhoneInfo>(() => (userInfo.value ?? {}) as ProfilePhoneInfo)
	const savedPhoneNumber = computed(
		() =>
			currentUserDetails.value?.phoneNumber ?? profilePhoneInfo.value.phone ?? profilePhoneInfo.value.phoneNumber ?? ""
	)
	const savedPhoneVerifiedAt = computed(
		() =>
			currentUserDetails.value?.phoneVerifiedAt ??
			profilePhoneInfo.value.phoneVerifiedAt ??
			profilePhoneInfo.value.phone_verified_at ??
			null
	)

	const normalizedPhoneNumberDraft = computed(() => normalizePhoneNumberForComparison(phoneNumberDraft.value))
	const normalizedSavedPhoneNumber = computed(() => normalizePhoneNumberForComparison(savedPhoneNumber.value))
	const formattedSavedPhoneNumber = computed(() => formatPhoneNumberForDisplay(savedPhoneNumber.value))
	const hasSavedPhoneNumber = computed(() => normalizedSavedPhoneNumber.value.length > 0)
	const phoneNumberDraftHasChanged = computed(
		() => normalizedPhoneNumberDraft.value !== normalizedSavedPhoneNumber.value
	)

	// Derived from the saved profile only, never from the draft: typing in the field must not
	// change what SMS preference gets persisted.
	const isPhoneVerified = computed(() => hasSavedPhoneNumber.value && Boolean(savedPhoneVerifiedAt.value))
	const isSmsPreferenceBlocked = computed(() => !isPhoneVerified.value)

	const isPhoneBusy = computed(
		() => isSavingPhoneNumber.value || isStartingPhoneVerification.value || isConfirmingPhoneVerification.value
	)
	const isPhoneVerificationPending = computed(
		() =>
			Boolean(phoneVerificationRequestedFor.value) &&
			normalizedSavedPhoneNumber.value === normalizePhoneNumberForComparison(phoneVerificationRequestedFor.value) &&
			!isPhoneVerified.value
	)
	const canSavePhoneNumber = computed(() => phoneNumberDraftHasChanged.value && !isPhoneBusy.value)
	// An unsaved number counts as unverified, so verifying it saves it first; it is only offered
	// once it has enough digits to plausibly be a number.
	const isPhoneVerificationOffered = computed(() =>
		phoneNumberDraftHasChanged.value
			? phoneNumberDraft.value.replace(/\D/g, "").length >= minimumPhoneDigits
			: hasSavedPhoneNumber.value && !isPhoneVerified.value
	)

	// Only the number shown in the field can be verified, so an unsaved edit has to be saved first.
	const canStartPhoneVerification = computed(
		() => hasSavedPhoneNumber.value && !isPhoneVerified.value && !phoneNumberDraftHasChanged.value && !isPhoneBusy.value
	)
	const canConfirmPhoneVerification = computed(
		() => isPhoneVerificationPending.value && phoneVerificationCode.value.trim().length > 0 && !isPhoneBusy.value
	)

	const phoneStep = computed(() => {
		if (!hasSavedPhoneNumber.value || isEditingPhoneNumber.value) return PhoneStep.Edit
		if (isPhoneVerified.value) return PhoneStep.Done
		return isPhoneVerificationPending.value ? PhoneStep.Confirm : PhoneStep.Verify
	})

	const formattedPhoneVerifiedAt = computed(() => {
		if (!savedPhoneVerifiedAt.value) return ""

		const date =
			savedPhoneVerifiedAt.value instanceof Date ? savedPhoneVerifiedAt.value : new Date(savedPhoneVerifiedAt.value)
		return Number.isNaN(date.getTime())
			? ""
			: new Intl.DateTimeFormat(locale.value === "en" ? "en-CA" : "fr-CA", { dateStyle: "medium" }).format(date)
	})

	const phoneStatusLabel = computed(() => {
		if (!hasSavedPhoneNumber.value) return t("my-profile.phone.status.missing")
		return isPhoneVerified.value ? t("my-profile.phone.status.verified") : t("my-profile.phone.status.unverified")
	})

	const phoneNote = computed(() => {
		if (!hasSavedPhoneNumber.value) return t("my-profile.phone.note.empty")
		if (isPhoneVerified.value && formattedPhoneVerifiedAt.value) {
			return t("my-profile.phone.note.verified", { date: formattedPhoneVerifiedAt.value })
		}
		return t("my-profile.phone.note.unverified")
	})

	const savePhoneNumberButtonLabel = computed(() =>
		isSavingPhoneNumber.value ? t("my-profile.phone.actions.saving") : t("my-profile.phone.actions.save")
	)

	const startPhoneVerificationButtonLabel = computed(() => {
		if (isStartingPhoneVerification.value) return t("my-profile.phone.actions.sending")
		if (isPhoneVerificationPending.value) return t("my-profile.phone.actions.resend")
		return t("my-profile.phone.actions.send-code")
	})

	const confirmPhoneVerificationButtonLabel = computed(() =>
		isConfirmingPhoneVerification.value
			? t("my-profile.phone.actions.confirming")
			: t("my-profile.phone.actions.confirm")
	)

	const resetPhoneVerificationState = () => {
		phoneVerificationRequestedFor.value = null
		phoneVerificationCode.value = ""
		phoneVerificationMessage.value = null
		phoneVerificationError.value = null
	}

	const phoneErrorMessage = (error: unknown, fallbackKey: string) =>
		error instanceof Error ? error.message : t(fallbackKey)

	const applySavedPhone = (phoneNumber: string | null | undefined, phoneVerifiedAt: string | null | undefined) => {
		const update = { phoneNumber: phoneNumber ?? null, phoneVerifiedAt: phoneVerifiedAt ?? null }
		store.CurrentUserBasicDetails = { ...(store.CurrentUserBasicDetails ?? {}), ...update }
		if (store.CurrentUserDetails) {
			store.CurrentUserDetails = { ...store.CurrentUserDetails, ...update }
		}
	}

	const editPhoneNumber = () => {
		resetPhoneVerificationState()
		isEditingPhoneNumber.value = true
	}

	const cancelPhoneNumberChange = () => {
		phoneNumberDraft.value = savedPhoneNumber.value
		isEditingPhoneNumber.value = false
		resetPhoneVerificationState()
	}

	const savePhoneNumberFlow = async () => {
		if (!store.isUserLoggedIn || !canSavePhoneNumber.value) return

		isSavingPhoneNumber.value = true
		phoneVerificationMessage.value = null
		phoneVerificationError.value = null
		try {
			const result = await apiClient.setPhoneNumber(store.userApiToken, phoneNumberDraft.value.trim() || null)
			applySavedPhone(result.phoneNumber, result.phoneVerifiedAt)
			phoneNumberDraft.value = result.phoneNumber ?? ""
			isEditingPhoneNumber.value = false
			phoneVerificationRequestedFor.value = null
			phoneVerificationCode.value = ""
			phoneVerificationMessage.value = t("my-profile.phone.messages.saved")
		} catch (error) {
			phoneVerificationError.value = phoneErrorMessage(error, "my-profile.phone.messages.save-failed")
		} finally {
			isSavingPhoneNumber.value = false
		}
	}

	const startPhoneVerificationFlow = async () => {
		if (!store.isUserLoggedIn || !canStartPhoneVerification.value) return

		isStartingPhoneVerification.value = true
		phoneVerificationMessage.value = null
		phoneVerificationError.value = null
		try {
			const result = await apiClient.startPhoneVerification(store.userApiToken)
			phoneVerificationRequestedFor.value = result.phoneNumber
			phoneVerificationCode.value = ""
			phoneVerificationMessage.value = t("my-profile.phone.messages.code-sent")
		} catch (error) {
			phoneVerificationError.value = phoneErrorMessage(error, "my-profile.phone.messages.start-failed")
		} finally {
			isStartingPhoneVerification.value = false
		}
	}

	// Verifying an unsaved number saves it first. A code is sent only when none is pending or
	// already on its way; asking for another one is an explicit resend through
	// startPhoneVerificationFlow.
	const requestPhoneVerificationCode = async () => {
		if (isPhoneBusy.value) return

		if (phoneNumberDraftHasChanged.value) {
			await savePhoneNumberFlow()
			if (phoneNumberDraftHasChanged.value) return
		} else if (isPhoneVerificationPending.value) {
			return
		}

		await startPhoneVerificationFlow()
	}

	const confirmPhoneVerificationFlow = async () => {
		if (!store.isUserLoggedIn || !canConfirmPhoneVerification.value) return

		isConfirmingPhoneVerification.value = true
		phoneVerificationMessage.value = null
		phoneVerificationError.value = null
		try {
			const result = await apiClient.confirmPhoneVerification(store.userApiToken, phoneVerificationCode.value)

			if (!result.approved) {
				phoneVerificationError.value = t("my-profile.phone.messages.invalid-code")
				return
			}

			applySavedPhone(result.phoneNumber, result.phoneVerifiedAt ?? new Date().toISOString())
			phoneNumberDraft.value = result.phoneNumber
			isEditingPhoneNumber.value = false
			resetPhoneVerificationState()
			phoneVerificationMessage.value = t("my-profile.phone.messages.verified")
			await store.fetchBasicCurrentUserDetails()
		} catch (error) {
			phoneVerificationError.value = phoneErrorMessage(error, "my-profile.phone.messages.confirm-failed")
		} finally {
			isConfirmingPhoneVerification.value = false
		}
	}

	watch(
		savedPhoneNumber,
		phoneNumber => {
			if (!isEditingPhoneNumber.value) {
				phoneNumberDraft.value = phoneNumber
			}
		},
		{ immediate: true }
	)

	return {
		phoneNumberDraft,
		phoneNumberDraftHasChanged,
		phoneVerificationCode,
		phoneVerificationMessage,
		phoneVerificationError,
		isSavingPhoneNumber,
		isStartingPhoneVerification,
		isConfirmingPhoneVerification,
		hasSavedPhoneNumber,
		formattedSavedPhoneNumber,
		isPhoneVerified,
		isSmsPreferenceBlocked,
		isPhoneVerificationPending,
		isPhoneVerificationOffered,
		canSavePhoneNumber,
		canStartPhoneVerification,
		canConfirmPhoneVerification,
		phoneStep,
		formattedPhoneVerifiedAt,
		phoneStatusLabel,
		phoneNote,
		savePhoneNumberButtonLabel,
		startPhoneVerificationButtonLabel,
		confirmPhoneVerificationButtonLabel,
		editPhoneNumber,
		cancelPhoneNumberChange,
		savePhoneNumberFlow,
		startPhoneVerificationFlow,
		requestPhoneVerificationCode,
		confirmPhoneVerificationFlow,
	}
}
